import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { DatabaseConnection } from 'services/database-connection-service';

/**
 * Database Configuration Dialog Component
 * 
 * A professional dialog for configuring database connections (PostgreSQL, MongoDB, MySQL).
 * Supports both add and edit modes with proper form validation.
 * 
 * Features:
 * - Form validation
 * - Password visibility toggle
 * - Loading states
 * - Error handling
 * - Professional UI design
 * 
 * @param isOpen - Whether the dialog is open
 * @param onClose - Callback when dialog closes
 * @param onSave - Callback when form is submitted with valid data
 * @param existingConnection - Existing connection data for edit mode
 * @param databaseType - Type of database (postgres, mongodb, mysql)
 * @param isLoading - Whether the save operation is in progress
 */

interface DatabaseConfigDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: DatabaseFormData) => void;
    existingConnection?: DatabaseConnection | null;
    databaseType: 'postgres' | 'mongodb' | 'mysql';
    isLoading?: boolean;
}

export interface DatabaseFormData {
    host: string;
    port: number;
    username: string;
    password: string;
    databaseName: string;
    connectionString: string;
}

const defaultPorts = {
    postgres: 5432,
    mongodb: 27017,
    mysql: 3306
};

const databaseLabels = {
    postgres: 'PostgreSQL',
    mongodb: 'MongoDB',
    mysql: 'MySQL'
};

export const DatabaseConfigDialog = ({
    isOpen,
    onClose,
    onSave,
    existingConnection,
    databaseType,
    isLoading = false
}: DatabaseConfigDialogProps) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const [useConnectionString, setUseConnectionString] = useState(false);

    const [formData, setFormData] = useState<DatabaseFormData>({
        host: '',
        port: defaultPorts[databaseType],
        username: '',
        password: '',
        databaseName: '',
        connectionString: ''
    });

    const [errors, setErrors] = useState<Partial<Record<keyof DatabaseFormData, string>>>({});

    // Load existing connection data
    useEffect(() => {
        if (existingConnection) {
            setFormData({
                host: existingConnection.connectionInfo.host || '',
                port: existingConnection.connectionInfo.port || defaultPorts[databaseType],
                username: existingConnection.connectionInfo.username || '',
                password: existingConnection.connectionInfo.password || '',
                databaseName: existingConnection.connectionInfo.databaseName || '',
                connectionString: existingConnection.connectionInfo.connectionString || ''
            });
            
            // If connection string exists, switch to connection string mode
            if (existingConnection.connectionInfo.connectionString) {
                setUseConnectionString(true);
            }
        } else {
            // Reset form for new connection
            setFormData({
                host: '',
                port: defaultPorts[databaseType],
                username: '',
                password: '',
                databaseName: '',
                connectionString: ''
            });
            setUseConnectionString(false);
        }
        setErrors({});
        setShowPassword(false);
    }, [existingConnection, databaseType, isOpen]);

    const handleInputChange = (field: keyof DatabaseFormData, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof DatabaseFormData, string>> = {};

        if (useConnectionString) {
            if (!formData.connectionString.trim()) {
                newErrors.connectionString = t('CONNECTION_STRING_REQUIRED') || 'Connection string is required';
            }
        } else {
            if (!formData.host.trim()) {
                newErrors.host = t('HOST_REQUIRED') || 'Host is required';
            }
            if (!formData.port || formData.port <= 0 || formData.port > 65535) {
                newErrors.port = t('VALID_PORT_REQUIRED') || 'Valid port is required (1-65535)';
            }
            if (!formData.username.trim()) {
                newErrors.username = t('USERNAME_REQUIRED') || 'Username is required';
            }
            if (!formData.password.trim()) {
                newErrors.password = t('PASSWORD_REQUIRED') || 'Password is required';
            }
            if (!formData.databaseName.trim()) {
                newErrors.databaseName = t('DATABASE_NAME_REQUIRED') || 'Database name is required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (validateForm()) {
            onSave(formData);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    const isEditMode = !!existingConnection;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-foreground">
                        {isEditMode 
                            ? t('EDIT_DATABASE_CONFIGURATION') || `Edit ${databaseLabels[databaseType]} Configuration`
                            : t('ADD_DATABASE_CONFIGURATION') || `Add ${databaseLabels[databaseType]} Configuration`
                        }
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground">
                        {isEditMode
                            ? t('UPDATE_DATABASE_CONNECTION_DETAILS') || 'Update your database connection details below.'
                            : t('ENTER_DATABASE_CONNECTION_DETAILS') || 'Enter your database connection details below.'
                        }
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4 space-y-6">
                    {/* Connection Type Toggle */}
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            onClick={() => setUseConnectionString(false)}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                !useConnectionString
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }`}
                            disabled={isLoading}
                        >
                            {t('INDIVIDUAL_FIELDS') || 'Individual Fields'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setUseConnectionString(true)}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                useConnectionString
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }`}
                            disabled={isLoading}
                        >
                            {t('CONNECTION_STRING') || 'Connection String'}
                        </button>
                    </div>

                    {useConnectionString ? (
                        // Connection String Mode
                        <div className="space-y-2">
                            <Label htmlFor="connectionString" className="text-sm font-medium text-foreground">
                                {t('CONNECTION_STRING') || 'Connection String'} <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="connectionString"
                                type="text"
                                placeholder={
                                    databaseType === 'postgres' 
                                        ? 'postgresql://username:password@host:port/database'
                                        : databaseType === 'mongodb'
                                        ? 'mongodb://username:password@host:port/database'
                                        : 'mysql://username:password@host:port/database'
                                }
                                value={formData.connectionString}
                                onChange={(e) => handleInputChange('connectionString', e.target.value)}
                                disabled={isLoading}
                                className={errors.connectionString ? 'border-destructive' : ''}
                            />
                            {errors.connectionString && (
                                <p className="text-xs text-destructive">{errors.connectionString}</p>
                            )}
                        </div>
                    ) : (
                        // Individual Fields Mode
                        <div className="space-y-4">
                            {/* Host */}
                            <div className="space-y-2">
                                <Label htmlFor="host" className="text-sm font-medium text-foreground">
                                    {t('HOST') || 'Host'} <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="host"
                                    type="text"
                                    placeholder="localhost or db.example.com"
                                    value={formData.host}
                                    onChange={(e) => handleInputChange('host', e.target.value)}
                                    disabled={isLoading}
                                    className={errors.host ? 'border-destructive' : ''}
                                />
                                {errors.host && (
                                    <p className="text-xs text-destructive">{errors.host}</p>
                                )}
                            </div>

                            {/* Port */}
                            <div className="space-y-2">
                                <Label htmlFor="port" className="text-sm font-medium text-foreground">
                                    {t('PORT') || 'Port'} <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="port"
                                    type="number"
                                    placeholder={defaultPorts[databaseType].toString()}
                                    value={formData.port}
                                    onChange={(e) => handleInputChange('port', parseInt(e.target.value) || 0)}
                                    disabled={isLoading}
                                    className={errors.port ? 'border-destructive' : ''}
                                    min="1"
                                    max="65535"
                                />
                                {errors.port && (
                                    <p className="text-xs text-destructive">{errors.port}</p>
                                )}
                            </div>

                            {/* Username */}
                            <div className="space-y-2">
                                <Label htmlFor="username" className="text-sm font-medium text-foreground">
                                    {t('USERNAME') || 'Username'} <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="username"
                                    type="text"
                                    placeholder="database_user"
                                    value={formData.username}
                                    onChange={(e) => handleInputChange('username', e.target.value)}
                                    disabled={isLoading}
                                    className={errors.username ? 'border-destructive' : ''}
                                    autoComplete="username"
                                />
                                {errors.username && (
                                    <p className="text-xs text-destructive">{errors.username}</p>
                                )}
                            </div>

                            {/* Password */}
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-sm font-medium text-foreground">
                                    {t('PASSWORD') || 'Password'} <span className="text-destructive">*</span>
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={formData.password}
                                        onChange={(e) => handleInputChange('password', e.target.value)}
                                        disabled={isLoading}
                                        className={`pr-10 ${errors.password ? 'border-destructive' : ''}`}
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                        disabled={isLoading}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="text-xs text-destructive">{errors.password}</p>
                                )}
                            </div>

                            {/* Database Name */}
                            <div className="space-y-2">
                                <Label htmlFor="databaseName" className="text-sm font-medium text-foreground">
                                    {t('DATABASE_NAME') || 'Database Name'} <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="databaseName"
                                    type="text"
                                    placeholder="my_database"
                                    value={formData.databaseName}
                                    onChange={(e) => handleInputChange('databaseName', e.target.value)}
                                    disabled={isLoading}
                                    className={errors.databaseName ? 'border-destructive' : ''}
                                />
                                {errors.databaseName && (
                                    <p className="text-xs text-destructive">{errors.databaseName}</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="flex flex-row justify-end gap-2 sm:gap-3">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isLoading}
                        className="min-w-[100px]"
                    >
                        {t('CANCEL') || 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isLoading}
                        className="min-w-[100px] bg-primary hover:bg-primary/90"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t('SAVING') || 'Saving...'}
                            </>
                        ) : (
                            t('SAVE') || 'Save'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

